import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';
import { 
  ArrowLeft, FileText, Download, Calendar, TrendingUp, 
  TrendingDown, BarChart3, FileSpreadsheet, Receipt,
  Loader2, CheckCircle2, AlertCircle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);
  const [equityCurve, setEquityCurve] = useState([]);
  const [monthlyReturns, setMonthlyReturns] = useState([]);
  const [strategyComparison, setStrategyComparison] = useState([]);
  const [selectedDays, setSelectedDays] = useState(30);

  useEffect(() => {
    loadData();
  }, [selectedDays]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [perfRes, equityRes, monthlyRes, strategyRes] = await Promise.all([
        api.get(`/history/performance?days=${selectedDays}`),
        api.get(`/history/equity-curve?days=${selectedDays}`),
        api.get('/history/monthly-returns?months=12'),
        api.get(`/history/strategy-comparison?days=${selectedDays}`)
      ]);
      
      setPerformanceData(perfRes.data);
      setEquityCurve(equityRes.data.equity_curve || []);
      setMonthlyReturns(monthlyRes.data.monthly_returns || []);
      setStrategyComparison(strategyRes.data.strategies || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (reportType) => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('apexion_token');
      const response = await fetch(
        `${API_URL}/api/reports/generate/${reportType}?days=${selectedDays}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error('Failed to generate report');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `apexion_${reportType}_report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Report download error:', error);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const MetricCard = ({ title, value, change, icon: Icon, positive }) => (
    <Card className="bg-[#15171C] border-[#1E293B]">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[#CBD5E1]">{title}</p>
            <p className={`text-2xl font-bold ${positive === true ? 'text-green-400' : positive === false ? 'text-red-400' : 'text-[#F5F7FA]'}`}>
              {value}
            </p>
            {change !== undefined && (
              <p className={`text-xs ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {change >= 0 ? '+' : ''}{change.toFixed(1)}% vs previous
              </p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${positive === true ? 'bg-green-500/10' : positive === false ? 'bg-red-500/10' : 'bg-[#2F6BFF]/10'}`}>
            <Icon className={`w-6 h-6 ${positive === true ? 'text-green-400' : positive === false ? 'text-red-400' : 'text-[#2F6BFF]'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2F6BFF] mx-auto mb-4"></div>
          <p className="text-[#94A3B8]">Loading reports...</p>
        </div>
      </div>
    );
  }

  const current = performanceData?.current || {};

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Button
            data-testid="reports-back-btn"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-[#94A3B8] hover:text-[#F5F7FA]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#94A3B8]" />
            <select
              value={selectedDays}
              onChange={(e) => setSelectedDays(Number(e.target.value))}
              className="bg-[#15171C] border border-[#1E293B] rounded px-3 py-1 text-sm"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last year</option>
            </select>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Page Title */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-semibold font-['Outfit'] mb-2">Reports & Analytics</h1>
          <p className="text-[#CBD5E1]">Download performance reports and analyze your trading history</p>
        </motion.div>

        {/* Report Download Cards */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          <Card className="bg-gradient-to-br from-[#2F6BFF]/20 to-[#2F6BFF]/5 border-[#2F6BFF]/50 hover:border-[#2F6BFF] transition-all cursor-pointer"
                onClick={() => !generating && downloadReport('summary')}>
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-[#2F6BFF]/20">
                  <BarChart3 className="w-8 h-8 text-[#2F6BFF]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">Summary Report</h3>
                  <p className="text-sm text-[#CBD5E1]">Performance overview with key metrics</p>
                </div>
                <Button 
                  data-testid="download-summary-btn"
                  variant="outline" 
                  size="sm"
                  disabled={generating}
                  className="border-[#2F6BFF] text-[#2F6BFF]"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 border-purple-500/50 hover:border-purple-500 transition-all cursor-pointer"
                onClick={() => !generating && downloadReport('detailed')}>
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-purple-500/20">
                  <FileSpreadsheet className="w-8 h-8 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">Detailed Report</h3>
                  <p className="text-sm text-[#CBD5E1]">Full trade history with analysis</p>
                </div>
                <Button 
                  data-testid="download-detailed-btn"
                  variant="outline" 
                  size="sm"
                  disabled={generating}
                  className="border-purple-500 text-purple-400"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/20 to-green-500/5 border-green-500/50 hover:border-green-500 transition-all cursor-pointer"
                onClick={() => !generating && downloadReport('tax')}>
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-green-500/20">
                  <Receipt className="w-8 h-8 text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">Tax Report</h3>
                  <p className="text-sm text-[#CBD5E1]">Realized gains/losses for tax filing</p>
                </div>
                <Button 
                  data-testid="download-tax-btn"
                  variant="outline" 
                  size="sm"
                  disabled={generating}
                  className="border-green-500 text-green-400"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Key Metrics */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <MetricCard 
            title="Total P&L" 
            value={formatCurrency(current.total_pnl || 0)} 
            icon={current.total_pnl >= 0 ? TrendingUp : TrendingDown}
            positive={current.total_pnl > 0}
          />
          <MetricCard 
            title="Win Rate" 
            value={`${(current.win_rate || 0).toFixed(1)}%`}
            icon={CheckCircle2}
            positive={current.win_rate >= 50}
          />
          <MetricCard 
            title="Max Drawdown" 
            value={`${(current.max_drawdown || 0).toFixed(1)}%`}
            icon={AlertCircle}
            positive={current.max_drawdown < 10 ? true : current.max_drawdown > 20 ? false : null}
          />
          <MetricCard 
            title="Sharpe Ratio" 
            value={(current.sharpe_ratio || 0).toFixed(2)}
            icon={BarChart3}
            positive={current.sharpe_ratio > 1}
          />
        </motion.div>

        {/* Equity Curve Chart */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardHeader>
              <CardTitle className="font-['Outfit']">Equity Curve</CardTitle>
              <CardDescription className="text-[#CBD5E1]">Your portfolio value over time</CardDescription>
            </CardHeader>
            <CardContent>
              {equityCurve.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={equityCurve}>
                    <defs>
                      <linearGradient id="equityGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2F6BFF" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#2F6BFF" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#CBD5E1" 
                      fontSize={12}
                      tickFormatter={(val) => val ? val.substring(5, 10) : ''}
                    />
                    <YAxis 
                      stroke="#CBD5E1" 
                      fontSize={12}
                      tickFormatter={(val) => `£${(val/1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#15171C', border: '1px solid #1E293B', color: '#F5F7FA' }}
                      labelStyle={{ color: '#CBD5E1' }}
                      formatter={(value) => [formatCurrency(value), 'Equity']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="equity" 
                      stroke="#2F6BFF" 
                      fill="url(#equityGradient)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-[#CBD5E1]">
                  No trading data available for equity curve
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Monthly Returns */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardHeader>
              <CardTitle className="font-['Outfit']">Monthly Returns</CardTitle>
              <CardDescription className="text-[#CBD5E1]">P&L breakdown by month</CardDescription>
            </CardHeader>
            <CardContent>
              {monthlyReturns.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={monthlyReturns}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#CBD5E1" 
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#CBD5E1" 
                      fontSize={12}
                      tickFormatter={(val) => `£${val}`}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#15171C', border: '1px solid #1E293B', color: '#F5F7FA' }}
                      labelStyle={{ color: '#CBD5E1' }}
                      formatter={(value, name) => [formatCurrency(value), name === 'pnl' ? 'P&L' : name]}
                    />
                    <Bar 
                      dataKey="pnl" 
                      fill="#2F6BFF"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-[#CBD5E1]">
                  No monthly data available
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Strategy Comparison */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardHeader>
              <CardTitle className="font-['Outfit']">Strategy Performance</CardTitle>
              <CardDescription className="text-[#CBD5E1]">Compare your strategies over the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              {strategyComparison.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#1E293B]">
                        <th className="text-left py-3 px-4 text-[#F5F7FA] font-medium">Strategy</th>
                        <th className="text-right py-3 px-4 text-[#F5F7FA] font-medium">Trades</th>
                        <th className="text-right py-3 px-4 text-[#F5F7FA] font-medium">P&L</th>
                        <th className="text-right py-3 px-4 text-[#F5F7FA] font-medium">Win Rate</th>
                        <th className="text-right py-3 px-4 text-[#F5F7FA] font-medium">Sharpe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {strategyComparison.map((strategy, idx) => (
                        <tr key={idx} className="border-b border-[#1E293B]/50 hover:bg-[#1E293B]/20">
                          <td className="py-3 px-4 font-medium text-[#F5F7FA]">{strategy.strategy?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}</td>
                          <td className="text-right py-3 px-4 text-[#CBD5E1]">{strategy.total_trades}</td>
                          <td className={`text-right py-3 px-4 font-medium ${strategy.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatCurrency(strategy.total_pnl)}
                          </td>
                          <td className={`text-right py-3 px-4 ${strategy.win_rate >= 50 ? 'text-green-400' : 'text-yellow-400'}`}>
                            {strategy.win_rate?.toFixed(1)}%
                          </td>
                          <td className={`text-right py-3 px-4 ${strategy.sharpe_ratio > 1 ? 'text-green-400' : 'text-[#94A3B8]'}`}>
                            {strategy.sharpe_ratio?.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-8 text-center text-[#CBD5E1]">
                  No strategy comparison data available. Start trading to see performance by strategy.
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ReportsPage;
